/**
 * Matter Payload Utilities
 *
 * Simple utility to transform cleared field values to the correct sentinel values
 * expected by the backend partial-update API contract.
 *
 * Backend contract for the matter's own scalar fields (PUT /matters/{id}):
 * - Text: "" to clear
 * - Number: -1 to clear (MatterService.SetOrClearDecimal treats any negative as a clear)
 * - Date: "0001-01-01T00:00:00" to clear
 * - Dropdown object: { id: null } to clear
 * - Guid (e.g. invoiceTemplateId): "00000000-0000-0000-0000-000000000000" to clear.
 *   Omitting the field entirely leaves the existing value untouched - it is NOT
 *   the same as clearing it.
 *
 * Custom fields (PUT /matters/{id}/customFields) are a DIFFERENT contract and none of
 * the sentinels above apply. MatterCustomFieldHandler assigns TextValue/NumberValue
 * verbatim, so the clear value is plain null - a -1 is stored as the number -1.
 */

export const CLEAR_DATE_VALUE = '0001-01-01T00:00:00';
export const CLEAR_NUMBER_VALUE = -1;
export const CLEAR_GUID_VALUE = '00000000-0000-0000-0000-000000000000';

/**
 * Prepares custom field values for the /customfields endpoint.
 * Transforms cleared values to the correct sentinel values.
 *
 * @param {Array} customFields - Array of custom field objects
 * @returns {Array} - Array with properly formatted clear values
 */
export function prepareCustomFieldsArray(customFields) {
  if (!Array.isArray(customFields)) {
    return customFields;
  }
  return customFields.map(cf => {
    const prepared = {
      id: cf.id
    };

    // Text custom fields - send "" to clear
    if (cf.type === 'text') {
      prepared.textValue = cf.textValue == null ? '' : cf.textValue;
    }
    if (cf.type === 'number') {
      prepared.numberValue = cf.numberValue == null || cf.numberValue === '' ? null : cf.numberValue;
    }

    // Enum/dropdown custom fields - send null to clear
    if (cf.type === 'enum') {
      prepared.enumValueId = cf.enumValueId || null;
    }
    return prepared;
  });
}

/**
 * True when the value can be parsed into a valid Date.
 * Empty/null are treated as invalid (they map to a clear sentinel on save).
 */
export function isValidDateValue(value) {
  if (value == null || value === '') return false;
  const date = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(date.getTime());
}

/**
 * Format a date value as a local `YYYY-MM-DD` string.
 * Accepts a Date or a string. Date-only strings (`YYYY-MM-DD...`) are returned
 * as-is (first 10 chars) to avoid timezone shifting a day. Returns '' when the
 * value cannot be parsed.
 */
export function toDateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Builds the body for `PUT /matters/{id}`, mirroring the Angular
 * `Matter.prototype.update()` contract: cleared values are transformed to the
 * backend's sentinel values so they are explicitly cleared rather than ignored.
 *
 * Origination step-down handling (dialog/omit-when-unchanged) is layered on by
 * the caller — this builder always emits the transformed origination values.
 *
 * @param {Object} matter - The (edited) matter object.
 * @param {Object} [options]
 * @param {boolean} [options.updateEntries] - Passed through as rateOptions.updateEntries.
 * @returns {Object} The request body.
 */
export function prepareMatterUpdatePayload(matter, {
  updateEntries
} = {}) {
  const transformText = value => value == null ? '' : value;
  const transformEnum = value => value == null || value === '' ? '' : value;
  const transformNumber = value => value == null || value === '' ? CLEAR_NUMBER_VALUE : value;
  const transformObjectDropdown = value => value == null || value.id == null ? {
    id: null
  } : value;
  const transformDate = value => isValidDateValue(value) ? toDateInputValue(value) : CLEAR_DATE_VALUE;
  const transformedCustom = matter.custom ? {
    department: transformText(matter.custom.department),
    practiceArea: transformText(matter.custom.practiceArea),
    referralSource: transformText(matter.custom.referralSource),
    adverse: transformText(matter.custom.adverse),
    relatedAdverse: transformText(matter.custom.relatedAdverse),
    relatedClients: transformText(matter.custom.relatedClients),
    opposingCounsel: transformText(matter.custom.opposingCounsel),
    instructions: transformText(matter.custom.instructions),
    email: transformText(matter.custom.email),
    emailCC: transformText(matter.custom.emailCC)
  } : matter.custom;
  const transformedBilling = matter.billing ? {
    ...matter.billing,
    originationStepDownStart: transformDate(matter.billing.originationStepDownStart),
    originationStepDownPeriodInMonths: transformNumber(matter.billing.originationStepDownPeriodInMonths)
  } : matter.billing;
  return {
    billing: transformedBilling,
    client: {
      id: matter.client?.id
    },
    matterTag: transformObjectDropdown(matter.matterTag),
    custom: transformedCustom,
    name: matter.name,
    opened: toDateInputValue(matter.opened) || matter.opened,
    restricted: matter.restricted,
    users: matter.users,
    reference: transformText(matter.reference),
    description: transformText(matter.description),
    externalId: transformText(matter.externalId),
    ledesId: transformText(matter.ledesId),
    ledesInvoiceDescription: transformText(matter.ledesInvoiceDescription),
    contact: matter.contact,
    rateOptions: {
      updateEntries
    },
    caseStatus: transformEnum(matter.caseStatus),
    marketingSource: transformEnum(matter.marketingSource),
    expectedSettlementAmount: transformNumber(matter.expectedSettlementAmount),
    expectedCaseCosts: transformNumber(matter.expectedCaseCosts),
    expectedSettlementDate: transformDate(matter.expectedSettlementDate),
    settlementDate: transformDate(matter.settlementDate)
  };
}